import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const AuthContext = createContext(null);

// SECURITY: auth tokens live in httpOnly cookies set by the backend.
// No tokens are stored in JS-accessible storage (sessionStorage/localStorage).
axios.defaults.withCredentials = true;

const PROFILE_KEY = 'userProfile';

// Response interceptor: silent token refresh on 401, billing redirect on 402
let _refreshPromise = null;
let _redirecting402 = false;

const AUTH_NO_RETRY = ['/auth/refresh', '/auth/login', '/auth/register', '/auth/logout'];

axios.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {};
    const status = error.response?.status;
    const url = original.url || '';

    if (status === 401 && !original._retried && !AUTH_NO_RETRY.some((p) => url.includes(p))) {
      original._retried = true;
      try {
        _refreshPromise = _refreshPromise || axios.post(`${API}/auth/refresh`, {});
        await _refreshPromise;
        _refreshPromise = null;
        return axios(original);
      } catch {
        _refreshPromise = null;
      }
    }

    if (
      status === 402 &&
      error.response?.data?.error === 'subscription_required' &&
      !_redirecting402 &&
      window.location.pathname !== '/billing'
    ) {
      _redirecting402 = true;
      window.location.replace('/billing');
    }
    return Promise.reject(error);
  }
);

async function hydratePreferences() {
  // Pull server-side personalisation (home country, footprint, etc.) into sessionStorage
  try {
    const { data } = await axios.get(`${API}/user-profile/me`);
    if (data?.exists && data.profile) {
      const local = JSON.parse(sessionStorage.getItem(PROFILE_KEY) || 'null');
      sessionStorage.setItem(PROFILE_KEY, JSON.stringify({ ...(local || {}), ...data.profile }));
    }
  } catch { /* non-blocking */ }
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/auth/me`);
      await hydratePreferences();
      setUser(data);
    } catch {
      setUser(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = async (email, password) => {
    const { data } = await axios.post(`${API}/auth/login`, { email, password });
    await hydratePreferences();
    setUser(data);
    return data;
  };

  const register = async (name, email, password, companyName, agreedToTerms = false) => {
    const { data } = await axios.post(`${API}/auth/register`, { name, email, password, company_name: companyName, agreed_to_terms: agreedToTerms });
    setUser(data);
    return data;
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`, {});
    } catch { /* ignore */ }
    sessionStorage.removeItem(PROFILE_KEY);
    setUser(false);
  };

  const changePassword = async (newPassword) => {
    const { data } = await axios.post(`${API}/auth/change-password`, { new_password: newPassword });
    await checkAuth();
    return data;
  };

  const setupMfa = async () => {
    const { data } = await axios.post(`${API}/auth/mfa/setup`, {});
    return data;
  };

  const verifyMfa = async (code) => {
    const { data } = await axios.post(`${API}/auth/mfa/verify`, { code });
    await checkAuth();
    return data;
  };

  const refreshUser = checkAuth;

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout, changePassword, setupMfa, verifyMfa, refreshUser }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
}

export function formatApiError(detail) {
  if (detail == null) return 'Something went wrong. Please try again.';
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail))
    return detail.map(e => (e && typeof e.msg === 'string' ? e.msg : JSON.stringify(e))).filter(Boolean).join(' ');
  if (detail && typeof detail.msg === 'string') return detail.msg;
  return String(detail);
}
